import React, { useState, useEffect } from 'react';
import { X, FolderKanban, Calendar } from 'lucide-react';
import { usePublishingStore } from '../../store/publishingStore';
import { Campaign, PlatformConnection } from '../../services/publishing.service';
import { toast } from 'sonner';
import { getPlatformIcon, getPlatformName, getPlatformColor } from './components/PlatformConnectionCard';

interface CampaignModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingCampaign: Campaign | null;
    platforms: PlatformConnection[];
}

const CampaignModal: React.FC<CampaignModalProps> = ({
    isOpen,
    onClose,
    editingCampaign,
    platforms,
}) => {
    const { createCampaign, updateCampaign, fetchCampaigns } = usePublishingStore();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        platformIds: [] as string[],
        status: 'draft',
        startDate: '',
        endDate: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const connectedPlatforms = platforms.filter(p => p.status === 'connected');

    useEffect(() => {
        if (editingCampaign) {
            setFormData({
                name: editingCampaign.name,
                description: editingCampaign.description || '',
                platformIds: Array.isArray(editingCampaign.platformIds)
                    ? editingCampaign.platformIds.map(p => (typeof p === 'string' ? p : p._id))
                    : [],
                status: editingCampaign.status,
                startDate: editingCampaign.startDate
                    ? new Date(editingCampaign.startDate).toISOString().split('T')[0]
                    : '',
                endDate: editingCampaign.endDate
                    ? new Date(editingCampaign.endDate).toISOString().split('T')[0]
                    : '',
            });
        } else {
            setFormData({
                name: '',
                description: '',
                platformIds: [],
                status: 'draft',
                startDate: '',
                endDate: '',
            });
        }
    }, [editingCampaign]);

    const togglePlatform = (platformId: string) => {
        setFormData(prev => ({
            ...prev,
            platformIds: prev.platformIds.includes(platformId)
                ? prev.platformIds.filter(id => id !== platformId)
                : [...prev.platformIds, platformId],
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Please enter a campaign name');
            return;
        }

        setIsSubmitting(true);

        try {
            const campaignData: {
                name: string;
                description?: string;
                platformIds?: string[];
                status?: 'draft' | 'active' | 'completed' | 'cancelled';
                startDate?: string;
                endDate?: string;
            } = {
                name: formData.name.trim(),
                status: formData.status as 'draft' | 'active' | 'completed' | 'cancelled',
            };

            if (formData.description.trim()) {
                campaignData.description = formData.description.trim();
            }
            if (formData.platformIds.length > 0) {
                campaignData.platformIds = formData.platformIds;
            }
            if (formData.startDate) {
                campaignData.startDate = formData.startDate;
            }
            if (formData.endDate) {
                campaignData.endDate = formData.endDate;
            }

            if (editingCampaign) {
                await updateCampaign(editingCampaign.id, campaignData as Partial<Campaign>);
            } else {
                await createCampaign(campaignData as Parameters<typeof createCampaign>[0]);
            }

            await fetchCampaigns();
            onClose();
        } catch (error) {
            console.error('Failed to save campaign:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-3xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                                <FolderKanban className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Campaign Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Product Launch, Weekly Tips"
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Describe the campaign goals and content (optional)"
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                            />
                        </div>

                        {/* Platform Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Target Platforms
                            </label>
                            {connectedPlatforms.length === 0 ? (
                                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl text-sm text-gray-500 dark:text-gray-400">
                                    No connected platforms available.
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {connectedPlatforms.map((platform) => (
                                        <button
                                            key={platform.id}
                                            type="button"
                                            onClick={() => togglePlatform(platform.id)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all ${formData.platformIds.includes(platform.id)
                                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                }`}
                                        >
                                            <div className={`w-6 h-6 ${getPlatformColor(platform.platform)} rounded-md flex items-center justify-center text-white`}>
                                                {getPlatformIcon(platform.platform, 'w-3 h-3')}
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {getPlatformName(platform.platform)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Status
                            </label>
                            <div className="flex gap-3">
                                {['draft', 'active', 'completed', 'cancelled'].map((status) => (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, status })}
                                        className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-colors capitalize ${formData.status === status
                                            ? status === 'active'
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                                : status === 'completed'
                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                                    : status === 'cancelled'
                                                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-150 dark:hover:bg-gray-650'
                                            }`}
                                    >
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CampaignModal;
